import type {KeyboardEvent, ReactNode} from 'react';

import {CheckOutlined, DownOutlined} from '@ant-design/icons';
import {useEffect, useId, useMemo, useRef, useState} from 'react';

import {cn} from '../../helpers/cn';

export type SelectOption = {
  label: ReactNode;
  value: string | number;
  disabled?: boolean;
};

export type SelectProps = {
  options?: SelectOption[];
  value?: string | number;
  defaultValue?: string | number;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  name?: string;
  onChange?: (value: string | number) => void;
};

export function Select({
  options = [],
  value,
  defaultValue,
  placeholder,
  disabled,
  className,
  id,
  name,
  onChange
}: SelectProps) {
  const generatedId = useId();
  const selectId = id ?? generatedId;
  const listboxId = `${selectId}-listbox`;
  const rootRef = useRef<HTMLDivElement>(null);
  const isControlled = value !== undefined;
  const [uncontrolledValue, setUncontrolledValue] = useState<string | number | undefined>(
    defaultValue
  );
  const currentValue = isControlled ? value : uncontrolledValue;
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);

  const selectedOption = options.find((opt) => String(opt.value) === String(currentValue));
  const hasValue = selectedOption != null;

  const enabledOptions = useMemo(() => options.filter((opt) => !opt.disabled), [options]);

  const emitChange = (next: string | number) => {
    if (!isControlled) {
      setUncontrolledValue(next);
    }
    onChange?.(next);
  };

  const close = () => {
    setOpen(false);
    setHighlightIndex(-1);
  };

  const selectOption = (option: SelectOption) => {
    if (option.disabled) return;
    emitChange(option.value);
    close();
  };

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        close();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const selectedIndex = enabledOptions.findIndex(
      (opt) => String(opt.value) === String(currentValue)
    );
    setHighlightIndex(selectedIndex >= 0 ? selectedIndex : 0);
  }, [open, currentValue, enabledOptions]);

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!open) {
          setOpen(true);
          return;
        }
        setHighlightIndex((prev) => Math.min(prev + 1, enabledOptions.length - 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!open) {
          setOpen(true);
          return;
        }
        setHighlightIndex((prev) => Math.max(prev - 1, 0));
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (!open) {
          setOpen(true);
          return;
        }
        if (highlightIndex >= 0 && enabledOptions[highlightIndex]) {
          selectOption(enabledOptions[highlightIndex]!);
        }
        break;
      case 'Escape':
        event.preventDefault();
        close();
        break;
      case 'Tab':
        close();
        break;
      default:
        break;
    }
  };

  return (
    <div ref={rootRef} className={cn('relative inline-flex w-full min-w-20', className)}>
      {name != null && (
        <input type="hidden" name={name} value={currentValue != null ? String(currentValue) : ''} />
      )}
      <button
        id={selectId}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        disabled={disabled}
        className={cn(
          'flex h-8 w-full cursor-pointer items-center justify-between gap-2 rounded-[6px] border border-disabled-border bg-white px-[11px] font-iransans text-[14px] transition-colors',
          'hover:border-orange focus-visible:border-orange focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange',
          'disabled:cursor-not-allowed disabled:border-disabled-border disabled:bg-disabled-surface disabled:text-disabled-fg disabled:hover:border-disabled-border',
          hasValue ? 'text-neutral-fg' : 'text-disabled-fg'
        )}
        onClick={() => {
          if (disabled) return;
          setOpen((prev) => !prev);
        }}
        onKeyDown={handleKeyDown}
      >
        <span className="min-w-0 truncate text-start">
          {hasValue ? selectedOption.label : placeholder}
        </span>
        <DownOutlined
          aria-hidden
          className={cn(
            'shrink-0 text-[12px] text-disabled-fg transition-transform duration-300 ease-in-out',
            open && 'rotate-180'
          )}
        />
      </button>
      <div
        id={listboxId}
        role="listbox"
        aria-hidden={!open}
        className={cn(
          'ds-dropdown-shadow absolute top-full z-50 mt-1 max-h-60 w-full origin-top overflow-auto rounded-[8px] border border-disabled-border bg-white p-1 transition-[transform,opacity,visibility] duration-200 ease-[cubic-bezier(0.08,0.82,0.17,1)]',
          open
            ? 'visible scale-y-100 opacity-100'
            : 'pointer-events-none invisible scale-y-[0.8] opacity-0'
        )}
      >
        {options.map((option) => {
          const selected = String(option.value) === String(currentValue);
          const enabledIndex = enabledOptions.findIndex(
            (opt) => String(opt.value) === String(option.value)
          );
          const highlighted = enabledIndex === highlightIndex;

          return (
            <div
              key={String(option.value)}
              role="option"
              aria-selected={selected}
              aria-disabled={option.disabled || undefined}
              className={cn(
                'flex min-h-8 cursor-pointer items-center justify-between rounded-[4px] px-3 py-[5px] text-[14px] leading-[22px] text-neutral-fg transition-colors duration-300',
                highlighted && !option.disabled && 'bg-disabled-surface',
                selected && 'bg-brand-surface font-semibold',
                option.disabled && 'cursor-not-allowed bg-transparent text-disabled-fg'
              )}
              onMouseEnter={() => {
                if (!option.disabled && enabledIndex >= 0) {
                  setHighlightIndex(enabledIndex);
                }
              }}
              onClick={() => selectOption(option)}
            >
              <span className="min-w-0 flex-1 truncate">{option.label}</span>
              {selected && (
                <CheckOutlined aria-hidden className="ms-2 shrink-0 text-[12px] text-orange" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
